import React from 'react';
import logo from './assets/logo.svg';
import { API_BASE_URL } from './config';

const roadmapNodes = [
  { label: 'Distributed Queue / Redis', detail: 'Optional Redis backend for multi-worker horizontal scaling — run thousands of concurrent workflows across many machines without code changes.', category: 'Scalability', accent: '#ef4444' },
  { label: 'Edge Runtime / IoT Mode', detail: 'Single-binary edge agent built from the same codebase with embedded SQLite, MQTT-native triggers, and cloud workspace sync — for Raspberry Pi and industrial gateways.', category: 'Deployment', accent: '#10b981' },
  { label: 'Rules Engine A/B Testing', detail: 'Run two rule set versions simultaneously on live traffic with a configurable split percentage. Compare decision outcomes before fully promoting a new version.', category: 'Rules', accent: '#8b5cf6' },
];

const LandingPage = ({ onLoginClick, onDocsClick, branding }) => {
  const brandName = branding?.app_name || 'xavvy';
  const brandLogo = branding?.logo_url || logo;
  const brandFooterText = branding?.footer_text || `© ${new Date().getFullYear()} ${brandName}. All rights reserved.`;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      backgroundColor: 'var(--bg-primary)',
      color: 'var(--text-primary)',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif"
    }}>
      {/* Header */}
      <header style={{
        padding: '20px 40px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid var(--border-color)',
        background: 'var(--bg-secondary)',
        position: 'sticky',
        top: 0,
        zIndex: 1000
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <img src={brandLogo} alt={brandName} style={{ height: '32px', maxWidth: '120px', objectFit: 'contain' }} />
          <span style={{ fontSize: '1.5rem', fontWeight: 700, letterSpacing: '-0.5px' }}>{brandName}</span>
        </div>
        <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
          <button
            onClick={onDocsClick}
            style={{
              textDecoration: 'none',
              color: 'var(--text-secondary)',
              fontWeight: 500,
              padding: '8px 16px',
              borderRadius: '4px',
              transition: 'background 0.2s',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: '1rem'
            }}
            onMouseOver={(e) => e.target.style.background = 'var(--bg-hover)'}
            onMouseOut={(e) => e.target.style.background = 'transparent'}
          >
            Documentation
          </button>
          <button
            onClick={onLoginClick}
            style={{
              background: 'var(--accent-color)',
              color: 'white',
              border: 'none',
              padding: '8px 24px',
              borderRadius: '6px',
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: '1rem',
              transition: 'transform 0.1s'
            }}
            onMouseDown={(e) => e.target.style.transform = 'scale(0.98)'}
            onMouseUp={(e) => e.target.style.transform = 'scale(1)'}
          >
            Login
          </button>
        </div>
      </header>

      <main style={{ flex: 1 }}>
        {/* Hero Section */}
        <section style={{
          padding: '80px 20px',
          textAlign: 'center',
          background: 'linear-gradient(180deg, var(--bg-secondary) 0%, var(--bg-primary) 100%)',
          borderBottom: '1px solid var(--border-color)'
        }}>
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <div style={{
              display: 'inline-block',
              padding: '7px 16px',
              borderRadius: '20px',
              background: 'linear-gradient(90deg, rgba(99,102,241,0.12), rgba(168,85,247,0.12))',
              border: '1px solid var(--border-color)',
              marginBottom: '24px',
              fontSize: '0.9rem',
              color: 'var(--accent-color)',
              fontWeight: 600
            }}>
              New: Rules Engine — decision tables, trees, scorecards, and simulation in one no-code designer
            </div>
            <h1 style={{
              fontSize: '4rem',
              marginBottom: '1.5rem',
              lineHeight: 1.1,
              fontWeight: 800,
              letterSpacing: '-1px'
            }}>
              Automation for Everyone —<br />
              <span style={{
                background: 'linear-gradient(90deg, var(--accent-color), #a855f7)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                Technical or Not
              </span>
            </h1>
            <p style={{
              fontSize: '1.35rem',
              color: 'var(--text-secondary)',
              marginBottom: '3rem',
              maxWidth: '700px',
              marginLeft: 'auto',
              marginRight: 'auto',
              lineHeight: 1.6
            }}>
              The open-source alternative to n8n, Make, Power Automate, and ActivePieces — without per-task pricing. Visual workflow automation, case management (BPM), reports &amp; dashboards, a no-code rules engine, AI agents, 140+ nodes, human-in-the-loop, real-time collaboration, and an intuitive builder anyone can use. Self-host on your own infrastructure.
            </p>

            <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', marginBottom: '40px' }}>
              <button
                onClick={onLoginClick}
                style={{
                  background: 'var(--accent-color)',
                  color: 'white',
                  border: 'none',
                  padding: '16px 40px',
                  borderRadius: '8px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: '1.1rem',
                  boxShadow: '0 4px 14px 0 rgba(0,0,0,0.1)'
                }}
              >
                Start Building for Free
              </button>
              <button
                onClick={onDocsClick}
                style={{
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-color)',
                  padding: '16px 40px',
                  borderRadius: '8px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: '1.1rem',
                  textDecoration: 'none'
                }}
              >
                View Documentation
              </button>
            </div>
          </div>
        </section>

        {/* Competitor comparison strip */}
        <section style={{ padding: '28px 20px', background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border-color)' }}>
          <div style={{ maxWidth: '900px', margin: '0 auto', display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'center', alignItems: 'center' }}>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-tertiary)', marginRight: '4px' }}>Compared with:</span>
            {['n8n', 'Make', 'Power Automate', 'ActivePieces', 'Zapier', 'Pabbly Connect'].map(name => (
              <span key={name} style={{ padding: '5px 14px', borderRadius: '999px', border: '1px solid var(--border-color)', fontSize: '0.85rem', color: 'var(--text-secondary)', background: 'var(--bg-primary)' }}>{name}</span>
            ))}
            <span style={{ fontSize: '0.85rem', color: 'var(--accent-color)', fontWeight: 600, marginLeft: '4px' }}>— self-hosted, no per-task pricing</span>
          </div>
        </section>

        {/* Features Section */}
        <section style={{ padding: '80px 20px', background: 'var(--bg-primary)' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <h2 style={{ textAlign: 'center', fontSize: '2.5rem', marginBottom: '16px', fontWeight: 700 }}>
              Why Choose {brandName}?
            </h2>
            <p style={{ textAlign: 'center', color: 'var(--text-secondary)', marginBottom: '60px', fontSize: '1.05rem' }}>
              Everything you need to automate — whether you write code or not.
            </p>

            {/* Hero features */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
              gap: '24px'
            }}>
              <FeatureCard
                icon="🎨"
                title="Visual Workflow Builder"
                description="Drag-and-drop canvas with branching, loops, merge, switch, and wait logic. Keyboard quick-add launcher, node groups, sticky notes, and live execution overlay — build anything without writing code."
              />
              <FeatureCard
                icon="🤖"
                title="Visual AI Agent Tooling"
                description="Draw an edge from the AI Agent's tools port to any other node — instantly wiring it as a callable tool. The agent understands each connected node's schema and calls them on demand during its ReAct reasoning loop."
              />
              <FeatureCard
                icon="🔌"
                title="75+ Built-in Integrations"
                description="Cloud storage (Google Drive, Dropbox, Box, OneDrive, S3, GCS, Azure Blob), payments (Stripe, PayPal, Square, QuickBooks, Xero), CRMs (Salesforce, HubSpot, Pipedrive, Zoho), marketing (Mailchimp, ActiveCampaign, Constant Contact), events (Eventbrite, Calendly), forms (Typeform), messaging (Slack, Teams, Discord, Telegram), Google + Microsoft 365 workspaces — and 50+ more. Each app covers the common Zapier modules out of the box."
              />
              <FeatureCard
                icon="⚡"
                title="One-Click Zapier / Make / n8n / Pipedream Import"
                description="Migrating from Zapier, Make, n8n, or Pipedream? Drop in your exported workflow JSON and xavvy rebuilds it on the canvas — mapping each step to the right node, wiring references between steps (including n8n's $node/$json expressions and Pipedream's steps.*), and showing a coverage report before you commit. Routers, paths, branches, filters, delays, schedules, and code steps are all converted. Connect your credentials and you're running."
              />
              <FeatureCard
                icon="🖊️"
                title="xavvy Forms Designer"
                description="A full-featured standalone form builder — 40+ element types including multi-step pages, conditional logic, repeating groups, rating scales, signatures, and rich text. Drag-and-drop canvas, live theming, and a public shareable URL."
              />
              <FeatureCard
                icon="👥"
                title="Human-in-the-Loop Flows"
                description="Pause runs for approvals or form input. Route decisions by role, notify via email with one-click response links, and resume with full interaction traceability."
              />
              <FeatureCard
                icon="🧱"
                title="Embedded Builder SDK"
                description="Embed the full xavvy workflow editor inside your own SaaS product with one script tag. Provision sessions via API, filter the node palette per tenant, and receive save/run events via postMessage."
              />
              <FeatureCard
                icon="⚖️"
                title="Full-Featured Rules Engine"
                description="Decision tables, condition/action rules, decision trees, and scorecards — all in one no-code designer. Group rules into ordered sets with per-group policies, run simulations against fixture sets, and get full evaluation traces for every decision."
              />
              <FeatureCard
                icon="📊"
                title="Reports & Dashboards Engine"
                description="Build live reports over cases, tasks, workflow runs, and SLA events — bar, line, pie, KPI, pivot, heatmap, scatter, gauge, and sparkline. Schedule delivery to email, Slack, Teams, or S3. Sign embed tokens so reports appear securely inside your own portals."
              />
              <FeatureCard
                icon="📋"
                title="casematrix — Case Management & BPM"
                description="Full case lifecycle: state machine, tasks, SLAs, comments, teams, and role-based in-baskets. A no-code layout designer lets admins build custom worker UIs from composable widgets — without writing React. Works standalone or wired into workflows."
              />
              <FeatureCard
                icon="📥"
                title="docuforge — Intelligent Document Processing"
                description="Ingest invoices, W-9s, IDs, and receipts via REST, email, S3, or watched folders. Six OCR + LLM-vision engines route per doc type; low-confidence fields surface in the xavvy Client review queue with an inline editor; corrections feed a training-data flywheel. Per-workspace daily cost cap and Enterprise fine-tuning included. Tools also publish to MCP."
              />
            </div>

            {/* All capabilities by category */}
            <div style={{ marginTop: '64px' }}>
              <h3 style={{ textAlign: 'center', fontSize: '1.6rem', fontWeight: 700, marginBottom: '12px', color: 'var(--text-primary)' }}>
                Everything else, built in
              </h3>
              <p style={{ textAlign: 'center', color: 'var(--text-tertiary)', marginBottom: '40px', fontSize: '0.95rem' }}>
                80+ capabilities across AI, scheduling, reports, case management, rules engine, data processing, and enterprise security.
              </p>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '20px'
              }}>
                <CompactCategory name="AI & Intelligence" accent="#a855f7" items={[
                  ['✨', 'AI Workflow Generator'],
                  ['⚡', 'AI Copilot for Reliability'],
                  ['🤖', 'AI Error Explainer'],
                  ['💬', 'AI Flow Chat Copilot'],
                  ['✨', 'AI Form Builder'],
                  ['💰', 'LLM Cost Tracking'],
                  ['🌐', 'Public Chat Agents'],
                  ['🔒', 'AI Governance Controls'],
                  ['🕸️', 'Multi-Agent Orchestration Studio'],
                  ['🔗', 'Agent-to-Agent Delegation Edges'],
                ]} />
                <CompactCategory name="Triggers & Scheduling" accent="#10b981" items={[
                  ['📝', 'Form Triggers'],
                  ['🔄', 'Polling Triggers'],
                  ['🧵', 'Kafka / Kinesis / Pub/Sub Streaming Triggers'],
                  ['⏰', 'Advanced Scheduler'],
                  ['📅', 'Scheduling Calendar'],
                  ['🛣️', 'Custom API Endpoints'],
                  ['🪝', 'Webhook Dashboard'],
                  ['🔗', 'MCP Server'],
                  ['⏱', 'Workflow SLA Alerts'],
                ]} />
                <CompactCategory name="Builder & UX" accent="#0ea5e9" items={[
                  ['🏗️', 'Template Gallery'],
                  ['✦', '55+ Expression Helpers'],
                  ['🎛️', 'No-Code Condition Builder'],
                  ['{}', 'Field Reference Picker'],
                  ['🪟', 'Step-by-Step View'],
                  ['🔍', 'Run Replay & Debugger'],
                  ['🔬', 'Execution Data Inspector'],
                  ['🔎', 'Pre-Run Validator'],
                  ['⌘', 'Command Palette'],
                  ['📊', 'Form Analytics'],
                ]} />
                <CompactCategory name="Data & Processing" accent="#f59e0b" items={[
                  ['📋', 'Built-in Tables'],
                  ['🗄️', 'Key-Value Data Store'],
                  ['⚙️', 'Data Processing Suite'],
                  ['🗄️', 'Warehouse Queries'],
                  ['🏢', 'CRM & Support Suite'],
                  ['🔁', 'Per-Node Retry & Rate Limits'],
                  ['♻️', 'Resume from Checkpoint'],
                  ['📊', 'Run Metrics & Alerting'],
                ]} />
                <CompactCategory name="DevOps & Collaboration" accent="#06b6d4" items={[
                  ['🌿', 'Git-Based Deployment'],
                  ['🌍', 'Multi-Environment Support'],
                  ['📁', 'Workflow Folders'],
                  ['🖱️', 'Real-time Collaboration'],
                  ['🧪', 'Test Suite & CI/CD'],
                  ['📱', 'Mobile Run Monitor'],
                  ['🔗', 'One-Click OAuth Connect'],
                  ['🖼️', 'Embeddable Form Widget'],
                ]} />
                <CompactCategory name="Enterprise & Security" accent="#ef4444" items={[
                  ['🔐', 'SSO / SAML / LDAP'],
                  ['🛡️', 'Two-Factor Auth'],
                  ['🌐', 'IP Allowlisting'],
                  ['📡', 'SIEM & Audit Streaming'],
                  ['🚨', 'SOAR Pack — Threat Enrichment + SIEM Triggers'],
                  ['🦅', 'CrowdStrike Falcon + Okta Incident Response'],
                  ['🟦', 'IBM QRadar + Microsoft Defender for Endpoint'],
                  ['🧿', 'Tanium Endpoint Actions + Sumo Logic Cloud SIEM'],
                  ['🛰️', 'Microsoft Sentinel Incidents + Splunk ES Notable Ack'],
                  ['🪪', 'Alert Dedupe (Fingerprint Window)'],
                  ['📋', 'SOAR Playbook Templates (Phishing / Lockout / CVE / S3)'],
                  ['🚦', 'Run Priority Classes (high/normal/low)'],
                  ['🧮', 'Redis Queue Backend (Multi-Worker Horizontal Scaling)'],
                  ['🎨', 'White-Label Branding'],
                  ['🧩', 'Plugin Governance'],
                  ['🔑', 'License Management'],
                  ['🏪', 'Community Marketplace'],
                ]} />
                <CompactCategory name="Reports & Dashboards" accent="#06b6d4" items={[
                  ['📊', 'Report Designer (10 viz types)'],
                  ['🗂️', 'Drag-Resize Dashboard Designer'],
                  ['📋', 'Pivot Tables & Conditional Formatting'],
                  ['⏰', 'Scheduled Delivery (email / Slack / Teams / S3)'],
                  ['🔔', 'Metric Alerts with Evaluation Log'],
                  ['🔒', 'Signed Embed Tokens for Portals'],
                  ['🛡️', 'Row-Level Security & Column Masking'],
                  ['🗄️', 'Custom SQL Datasets'],
                  ['📦', 'Report & Dashboard Template Library'],
                  ['⚙️', 'report_run / report_export Workflow Nodes'],
                ]} />
                <CompactCategory name="Rules Engine" accent="#8b5cf6" items={[
                  ['📋', 'Decision Tables (Spreadsheet if-then)'],
                  ['⚡', 'Condition / Action Rules (When → Then)'],
                  ['🌳', 'Decision Trees (Visual binary trees)'],
                  ['📊', 'Scorecards (Weighted-sum scoring)'],
                  ['📦', 'Rule Sets (Ordered execution groups)'],
                  ['🔖', 'Draft / Publish versioning'],
                  ['🧪', 'Simulator (single input + fixture sets)'],
                  ['🔍', 'Evaluation Trace Explorer'],
                  ['🔗', 'Trigger pre-filter rule set wiring'],
                  ['⚙️', 'Rule Evaluate / Validate workflow nodes'],
                ]} />
                <CompactCategory name="docuforge (IDP)" accent="#f43f5e" items={[
                  ['📥', 'Email / S3 / SharePoint / Drive / Dropbox Sources'],
                  ['🧾', '13-Type Prebuilt Catalog (Invoice / W-9 / 1099 / DL / ACORD…)'],
                  ['🛠️', 'Custom Doc Type Wizard + Fewshot Examples'],
                  ['🔀', 'Multi-Doc PDF Split + Auto Classify'],
                  ['🤖', 'LLM Vision + Tesseract + Textract + Azure DI + Document AI + Mistral'],
                  ['✍️', 'Handwriting Routing (Document AI primary)'],
                  ['🧑‍⚖️', 'Review Queue + Docuforge inbox in xavvy Client'],
                  ['📊', 'Accuracy Grid / Throughput Funnel / Cost Breakdown'],
                  ['💰', 'Daily Cost Guardrail (per workspace)'],
                  ['🧪', 'Fine-Tune Jobs + Few-Shot Promotion'],
                  ['🔗', 'docuforge.extract / docuforge.list_docs MCP Tools'],
                ]} />
                <CompactCategory name="Process Intelligence" accent="#06b6d4" items={[
                  ['🧭', 'Directly-Follows-Graph Discovery'],
                  ['🛤️', 'Variant Clustering (top-K execution paths)'],
                  ['🐢', 'Bottleneck Ranking (z-score + SLA contribution)'],
                  ['✅', 'Conformance Token-Replay vs Designed Graph'],
                  ['🌡️', 'DFG Heatmap Overlay (Conformance / Errors / Volume / Latency)'],
                  ['📐', 'Dashboard Widget Pack (DFG / Variants / Bottlenecks)'],
                  ['🧪', 'Monte Carlo What-If Simulation Sandbox'],
                  ['🔮', 'Predictive Next-Step Markov on Live Runs'],
                  ['🔒', 'Tier-Gated (Community / Pro / Enterprise / casematrix)'],
                ]} />
                <CompactCategory name="casematrix (Case Management)" accent="#10b981" items={[
                  ['📋', 'Case Types & State Machine'],
                  ['📊', 'Case Dashboard & KPIs'],
                  ['✅', 'Tasks & Milestones'],
                  ['💬', 'Comments & Timeline'],
                  ['👥', 'Teams & Auto-Assignment'],
                  ['🔗', 'Case Relations & Escalation'],
                  ['📌', 'Saved Views & Filters'],
                  ['⚡', 'Bulk Operations'],
                  ['🔄', 'Case Trigger Nodes'],
                  ['✏️', 'Case Action Nodes'],
                  ['@', '@Mention Notifications'],
                  ['📧', 'Email-to-Case Integration'],
                  ['🔍', 'Global Case Search'],
                  ['💾', 'Saved Filter Views'],
                  ['⚙️', 'Comment, Document & Field Triggers'],
                  ['🧑‍💼', 'xavvy Client Worker UI'],
                  ['🔒', 'Case-Level Lock & Heartbeat'],
                  ['🎭', 'Case Roles & Role-Scoped Inbox'],
                  ['🎨', 'Drag-Drop Case Layout Designer'],
                  ['🧱', 'Plugin Widgets (Custom React)'],
                  ['🏬', 'Vertical Layout Templates (Insurance / Legal / HR)'],
                ]} />
              </div>
            </div>
          </div>
        </section>


        {/* Roadmap section */}
        <section style={{ padding: '74px 20px', background: 'linear-gradient(180deg, var(--bg-secondary) 0%, rgba(99,102,241,0.06) 100%)', borderTop: '1px solid var(--border-color)', borderBottom: '1px solid var(--border-color)' }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '14px' }}>
              <span style={{ display: 'inline-block', padding: '5px 12px', borderRadius: '999px', fontSize: '0.82rem', fontWeight: 600, color: 'var(--accent-color)', border: '1px solid var(--border-color)', background: 'var(--bg-primary)' }}>
                Q3 – Q4 2026 Roadmap
              </span>
            </div>
            <h2 style={{ textAlign: 'center', fontSize: '2.2rem', marginBottom: '16px', fontWeight: 700 }}>
              What's Coming Next
            </h2>
            <p style={{ textAlign: 'center', color: 'var(--text-secondary)', marginBottom: '34px', fontSize: '1.05rem' }}>
              Four remaining items on the path to a complete, enterprise-grade automation platform.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px' }}>
              {roadmapNodes.map((node) => (
                <RoadmapTag key={node.label} label={node.label} detail={node.detail} category={node.category} accent={node.accent} />
              ))}
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section style={{ padding: '80px 20px', background: 'var(--bg-primary)', borderTop: '1px solid var(--border-color)' }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '14px' }}>
              <span style={{ display: 'inline-block', padding: '5px 12px', borderRadius: '999px', fontSize: '0.82rem', fontWeight: 600, color: 'var(--accent-color)', border: '1px solid var(--border-color)', background: 'var(--bg-secondary)' }}>
                Pricing
              </span>
            </div>
            <h2 style={{ textAlign: 'center', fontSize: '2.2rem', fontWeight: 700, marginBottom: '12px' }}>
              One price. No surprises.
            </h2>
            <p style={{ textAlign: 'center', color: 'var(--text-secondary)', marginBottom: '48px', fontSize: '1.05rem' }}>
              Self-host for free forever, or let us run it for you. No per-task fees, no workflow limits.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px', alignItems: 'stretch' }}>
              <PricingCard
                name="Community"
                price="Free"
                period="forever"
                description="The full xavvy engine, open-source and self-hosted. No licence key required."
                highlight={false}
                cta="Get Started"
                onCta={onLoginClick}
                features={[
                  '5 active workflows, 1,000 runs/month',
                  'All 100+ nodes included',
                  'AI Agent & LLM nodes',
                  'Webhook, schedule & form triggers',
                  'Multi-user workspaces',
                  'SQLite or Postgres database',
                  'Community support (GitHub)',
                ]}
              />
              <PricingCard
                name="Cloud"
                price="$9"
                period="per seat / month"
                description="xavvy hosted and managed for you. Zero DevOps — just build automations."
                highlight={true}
                cta="Start Free Trial"
                onCta={onLoginClick}
                features={[
                  'Unlimited workflows & executions',
                  'AI Copilot, error explainer & form builder',
                  'SSO / SAML / LDAP',
                  'Git sync & multi-environment support',
                  'Workflow folders & analytics',
                  'Audit log export & SIEM streaming',
                  'Priority email support',
                ]}
              />
              <PricingCard
                name="Enterprise"
                price="Custom"
                period="annual contract"
                description="For teams with custom compliance, volume, or white-label requirements."
                highlight={false}
                cta="Contact Sales"
                onCta={onLoginClick}
                features={[
                  'Everything in Cloud',
                  'Unlimited seats',
                  'White-label & custom domain',
                  'Dedicated infrastructure',
                  'IP allowlist & SIEM',
                  'SLA with guaranteed response time',
                  'Dedicated customer success manager',
                ]}
              />
            </div>
            <p style={{ textAlign: 'center', color: 'var(--text-tertiary)', marginTop: '32px', fontSize: '0.9rem' }}>
              All plans include all nodes. Cloud pricing billed annually; monthly billing available at 20 % premium.
            </p>
          </div>
        </section>

        {/* CTA Section */}
        <section style={{
          padding: '80px 20px',
          textAlign: 'center',
          background: 'var(--bg-secondary)',
          borderTop: '1px solid var(--border-color)'
        }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <h2 style={{ fontSize: '2.5rem', marginBottom: '20px' }}>Stop paying per task. Start owning your automation.</h2>
            <p style={{ fontSize: '1.2rem', color: 'var(--text-secondary)', marginBottom: '40px' }}>
              Whether you're technical or not — xavvy gives you the tools to automate anything, on your own infrastructure, with no usage caps and no vendor lock-in.
            </p>
            <button
              onClick={onLoginClick}
              style={{
                background: 'var(--text-primary)',
                color: 'var(--bg-primary)',
                border: 'none',
                padding: '16px 40px',
                borderRadius: '8px',
                fontWeight: 600,
                cursor: 'pointer',
                fontSize: '1.1rem'
              }}
            >
              Get Started Now
            </button>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer style={{
        padding: '40px 20px',
        textAlign: 'center',
        color: 'var(--text-tertiary)',
        borderTop: '1px solid var(--border-color)',
        fontSize: '0.9rem',
        background: 'var(--bg-primary)'
      }}>
        <div style={{ marginBottom: '20px' }}>
          <img src={brandLogo} alt={brandName} style={{ height: '24px', maxWidth: '90px', objectFit: 'contain', opacity: 0.5 }} />
        </div>
        <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'center', gap: '24px', fontSize: '0.85rem' }}>
          <a href="/privacy" style={{ color: 'var(--text-tertiary)', textDecoration: 'none' }} onMouseEnter={e => e.target.style.color = 'var(--accent-color)'} onMouseLeave={e => e.target.style.color = 'var(--text-tertiary)'}>Privacy Policy</a>
          <a href="/terms" style={{ color: 'var(--text-tertiary)', textDecoration: 'none' }} onMouseEnter={e => e.target.style.color = 'var(--accent-color)'} onMouseLeave={e => e.target.style.color = 'var(--text-tertiary)'}>Terms of Service</a>
        </div>
        <p>{brandFooterText}</p>
      </footer>
    </div>
  );
};

const FeatureCard = ({ icon, title, description }) => (
  <div style={{
    background: 'var(--bg-secondary)',
    padding: '30px',
    borderRadius: '12px',
    border: '1px solid var(--border-color)',
    transition: 'transform 0.2s, box-shadow 0.2s',
    cursor: 'default'
  }}
  onMouseEnter={(e) => {
    e.currentTarget.style.transform = 'translateY(-5px)';
    e.currentTarget.style.boxShadow = 'var(--shadow-strong)';
    e.currentTarget.style.borderColor = 'var(--accent-color)';
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.transform = 'translateY(0)';
    e.currentTarget.style.boxShadow = 'none';
    e.currentTarget.style.borderColor = 'var(--border-color)';
  }}
  >
    <div style={{ fontSize: '3rem', marginBottom: '20px' }}>{icon}</div>
    <h3 style={{ fontSize: '1.5rem', marginBottom: '10px', color: 'var(--text-primary)' }}>{title}</h3>
    <p style={{ fontSize: '1rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>{description}</p>
  </div>
);

const CompactCategory = ({ name, accent, items }) => (
  <div style={{
    background: 'var(--bg-secondary)',
    borderRadius: '12px',
    border: '1px solid var(--border-color)',
    padding: '24px',
    transition: 'border-color 0.2s',
  }}
  onMouseEnter={(e) => e.currentTarget.style.borderColor = accent}
  onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border-color)'}
  >
    <h4 style={{
      fontSize: '0.85rem',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      color: accent,
      marginBottom: '16px',
      paddingBottom: '10px',
      borderBottom: `2px solid ${accent}22`,
    }}>{name}</h4>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {items.map(([icon, title]) => (
        <div key={title} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
          <span style={{ fontSize: '0.95rem', flexShrink: 0, width: '20px', textAlign: 'center' }}>{icon}</span>
          <span>{title}</span>
        </div>
      ))}
    </div>
  </div>
);

const RoadmapTag = ({ label, detail, category, accent }) => (
  <div style={{
    padding: '16px 18px',
    borderRadius: '12px',
    border: '1px solid var(--border-color)',
    background: 'var(--bg-primary)',
    transition: 'transform 0.2s, box-shadow 0.2s, border-color 0.2s',
    cursor: 'default'
  }}
  onMouseEnter={(e) => {
    e.currentTarget.style.transform = 'translateY(-4px)';
    e.currentTarget.style.boxShadow = 'var(--shadow-strong)';
    e.currentTarget.style.borderColor = accent;
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.transform = 'translateY(0)';
    e.currentTarget.style.boxShadow = 'none';
    e.currentTarget.style.borderColor = 'var(--border-color)';
  }}>
    <div style={{ display: 'inline-block', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.03em', textTransform: 'uppercase', color: accent, background: `${accent}1A`, border: `1px solid ${accent}4D`, borderRadius: '999px', padding: '3px 8px', marginBottom: '10px' }}>{category}</div>
    <div style={{ fontWeight: 600, marginBottom: '4px', color: 'var(--text-primary)' }}>{label}</div>
    <div style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', lineHeight: 1.5 }}>{detail}</div>
  </div>
);

const PricingCard = ({ name, price, period, description, highlight, cta, onCta, features }) => (
  <div style={{
    background: highlight ? 'linear-gradient(160deg, rgba(99,102,241,0.12) 0%, rgba(168,85,247,0.10) 100%)' : 'var(--bg-secondary)',
    border: highlight ? '2px solid var(--accent-color)' : '1px solid var(--border-color)',
    borderRadius: '16px',
    padding: '36px 32px',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden',
  }}>
    {highlight && (
      <div style={{
        position: 'absolute', top: '16px', right: '16px',
        background: 'linear-gradient(90deg, var(--accent-color), #a855f7)',
        color: 'white', fontSize: '0.72rem', fontWeight: 700,
        padding: '3px 10px', borderRadius: '999px', letterSpacing: '0.05em', textTransform: 'uppercase'
      }}>Most Popular</div>
    )}
    <div style={{ marginBottom: '8px', fontWeight: 700, fontSize: '1rem', color: 'var(--accent-color)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{name}</div>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', marginBottom: '4px' }}>
      <span style={{ fontSize: '2.8rem', fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1 }}>{price}</span>
      {price !== 'Custom' && <span style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>/{period}</span>}
    </div>
    {price === 'Custom' && <div style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', marginBottom: '4px' }}>{period}</div>}
    <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', lineHeight: 1.5, marginBottom: '28px', marginTop: '8px' }}>{description}</p>
    <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 32px 0', flex: 1 }}>
      {features.map(f => (
        <li key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '10px', fontSize: '0.95rem', color: 'var(--text-secondary)' }}>
          <span style={{ color: '#10b981', fontWeight: 700, flexShrink: 0, marginTop: '1px' }}>✓</span>
          {f}
        </li>
      ))}
    </ul>
    <button
      onClick={onCta}
      style={{
        background: highlight ? 'linear-gradient(90deg, var(--accent-color), #a855f7)' : 'transparent',
        color: highlight ? 'white' : 'var(--text-primary)',
        border: highlight ? 'none' : '1px solid var(--border-color)',
        borderRadius: '8px',
        padding: '13px 0',
        fontWeight: 600,
        fontSize: '1rem',
        cursor: 'pointer',
        width: '100%',
        transition: 'opacity 0.15s',
      }}
      onMouseOver={(e) => e.currentTarget.style.opacity = '0.85'}
      onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
    >{cta}</button>
  </div>
);

export default LandingPage;


// ── Legal page shared layout ───────────────────────────────────────────────
const LegalPage = ({ title, children, branding }) => {
  const brandName = branding?.app_name || 'xavvy';
  const brandLogo = branding?.logo_url || logo;
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontFamily: 'Inter, system-ui, sans-serif' }}>
      <header style={{ padding: '20px 40px', borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', gap: '12px' }}>
        <a href="/" style={{ display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none', color: 'var(--text-primary)' }}>
          <img src={brandLogo} alt={brandName} style={{ height: '28px' }} />
          <span style={{ fontWeight: 700, fontSize: '1.1rem' }}>{brandName}</span>
        </a>
      </header>
      <main style={{ maxWidth: '800px', margin: '0 auto', padding: '48px 24px 80px' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 700, marginBottom: '8px' }}>{title}</h1>
        <p style={{ color: 'var(--text-tertiary)', fontSize: '0.9rem', marginBottom: '40px' }}>Last updated: April 17, 2026</p>
        <div style={{ lineHeight: 1.8, fontSize: '0.95rem', color: 'var(--text-secondary)' }}>
          {children}
        </div>
      </main>
      <footer style={{ padding: '24px 20px', textAlign: 'center', color: 'var(--text-tertiary)', borderTop: '1px solid var(--border-color)', fontSize: '0.85rem' }}>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '24px', marginBottom: '8px' }}>
          <a href="/privacy" style={{ color: 'var(--text-tertiary)', textDecoration: 'none' }}>Privacy Policy</a>
          <a href="/terms" style={{ color: 'var(--text-tertiary)', textDecoration: 'none' }}>Terms of Service</a>
          <a href="/" style={{ color: 'var(--text-tertiary)', textDecoration: 'none' }}>Home</a>
        </div>
        <p>&copy; {new Date().getFullYear()} {brandName}. All rights reserved.</p>
      </footer>
    </div>
  );
};

const SectionHeading = ({ children }) => (
  <h2 style={{ fontSize: '1.3rem', fontWeight: 700, marginTop: '36px', marginBottom: '12px', color: 'var(--text-primary)' }}>{children}</h2>
);

const SubHeading = ({ children }) => (
  <h3 style={{ fontSize: '1.05rem', fontWeight: 600, marginTop: '24px', marginBottom: '8px', color: 'var(--text-primary)' }}>{children}</h3>
);


// ── Privacy Policy ─────────────────────────────────────────────────────────
export const PrivacyPolicyPage = ({ branding }) => {
  const brandName = branding?.app_name || 'xavvy';
  return (
    <LegalPage title="Privacy Policy" branding={branding}>
      <p>
        This Privacy Policy describes how {brandName} ("{brandName}", "we", "us", or "our") collects,
        uses, and protects information when you use our workflow automation platform and related services
        (the "Service"). We are committed to protecting your privacy and handling your data transparently.
      </p>

      <SectionHeading>1. Information We Collect</SectionHeading>

      <SubHeading>1.1 Account Information</SubHeading>
      <p>
        When you create an account, we collect your email address and a password (stored as a salted
        hash — we never store plaintext passwords). If you sign in via SSO/SAML/LDAP, we receive
        the identity attributes your identity provider shares (typically email and display name).
      </p>

      <SubHeading>1.2 Workflow Data</SubHeading>
      <p>
        Workflow definitions, run logs, node configurations, and execution outputs are stored in your
        instance's database. For self-hosted deployments, this data never leaves your infrastructure.
        For our hosted Cloud offering, workflow data is stored in our managed infrastructure and
        encrypted at rest.
      </p>

      <SubHeading>1.3 Third-Party Account Connections (OAuth)</SubHeading>
      <p>
        When you connect third-party services (Google, Microsoft, Slack, GitHub, Notion, HubSpot, Linear)
        via OAuth, we receive an <strong>access token</strong> and, where applicable, a <strong>refresh token</strong> from
        the provider. We do <strong>not</strong> receive or store your third-party password. The specific
        data we can access depends on the scopes you authorize:
      </p>
      <ul style={{ paddingLeft: '24px', margin: '12px 0' }}>
        <li><strong>Google</strong>: Google Sheets (read/write spreadsheets), Gmail (send emails), Google Drive (read/write files), Google Calendar (read/write events). Scopes: <code>spreadsheets</code>, <code>gmail.send</code>, <code>drive</code>, <code>calendar</code>.</li>
        <li><strong>Microsoft</strong>: Outlook (send mail), OneDrive/Excel (read/write files), User profile (read). Scopes: <code>Mail.Send</code>, <code>Files.ReadWrite</code>, <code>User.Read</code>.</li>
        <li><strong>Slack</strong>: Read channels, send messages, upload files, read user profiles. Scopes: <code>channels:read</code>, <code>chat:write</code>, <code>files:write</code>, <code>users:read</code>.</li>
        <li><strong>GitHub</strong>: Repository access, user profile. Scopes: <code>repo</code>, <code>user</code>.</li>
        <li><strong>Notion</strong>: Read and write pages and databases you share with the integration.</li>
        <li><strong>HubSpot</strong>: Read/write contacts, read deals, manage content.</li>
        <li><strong>Linear</strong>: Read/write issues.</li>
      </ul>

      <SubHeading>1.4 Usage Data</SubHeading>
      <p>
        We collect basic usage metrics: workflow run counts, node types used, error rates, and
        feature adoption. This data is aggregated and does not include the content of your
        workflows or the data they process. Self-hosted instances do not send telemetry unless
        you explicitly enable it.
      </p>

      <SectionHeading>2. How We Use Your Information</SectionHeading>
      <p>We use the information we collect to:</p>
      <ul style={{ paddingLeft: '24px', margin: '12px 0' }}>
        <li>Provide, maintain, and improve the Service</li>
        <li>Execute your workflows by connecting to third-party services on your behalf using the OAuth tokens you authorize</li>
        <li>Automatically refresh expired OAuth tokens so your workflows continue to run without manual re-authorization</li>
        <li>Send transactional emails (password resets, SLA alerts, workflow failure notifications)</li>
        <li>Diagnose technical issues and prevent abuse</li>
      </ul>
      <p>
        We do <strong>not</strong> sell, rent, or share your personal data or third-party access tokens
        with advertisers or data brokers. We do <strong>not</strong> use your connected account data
        (emails, files, spreadsheets) for any purpose other than executing the workflow actions you configure.
      </p>

      <SectionHeading>3. How We Store and Protect Your Data</SectionHeading>
      <ul style={{ paddingLeft: '24px', margin: '12px 0' }}>
        <li><strong>Encryption at rest</strong>: All credentials and OAuth tokens are encrypted using Fernet symmetric encryption before storage. The encryption key is unique to your instance.</li>
        <li><strong>Encryption in transit</strong>: All connections use TLS/HTTPS.</li>
        <li><strong>Credential isolation</strong>: Credentials are scoped to individual users. Other users in your workspace cannot view or decrypt your stored tokens.</li>
        <li><strong>Self-hosted control</strong>: For self-hosted deployments, all data (credentials, workflow definitions, run logs) remains entirely on your infrastructure. We have no access to it.</li>
      </ul>

      <SectionHeading>4. Third-Party Services</SectionHeading>
      <p>
        {brandName} integrates with third-party services only when you explicitly connect them and
        configure workflows to use them. Each third-party connection operates under that service's
        own terms of service and privacy policy. We access only the data and perform only the actions
        that the OAuth scopes permit and that your workflow configuration specifies.
      </p>

      <SectionHeading>5. Data Retention</SectionHeading>
      <ul style={{ paddingLeft: '24px', margin: '12px 0' }}>
        <li><strong>Credentials</strong>: Stored until you delete them from the Credential Manager.</li>
        <li><strong>Workflow run logs</strong>: Retained according to your instance's retention settings (configurable).</li>
        <li><strong>Account data</strong>: Retained while your account is active. Upon deletion, all associated data is permanently removed.</li>
      </ul>

      <SectionHeading>6. Your Rights and Choices</SectionHeading>
      <ul style={{ paddingLeft: '24px', margin: '12px 0' }}>
        <li><strong>Revoke access</strong>: You can disconnect any third-party service at any time by deleting the credential in {brandName}. You can also revoke access from the third-party provider's settings (e.g., Google Account &gt; Security &gt; Third-party apps).</li>
        <li><strong>Export your data</strong>: Workflow definitions can be exported as JSON bundles at any time.</li>
        <li><strong>Delete your account</strong>: Contact your instance administrator to delete your account and all associated data.</li>
        <li><strong>Access and correction</strong>: You can view and update your account information at any time through the Settings page.</li>
      </ul>

      <SectionHeading>7. Google API Services User Data Policy</SectionHeading>
      <p>
        {brandName}'s use and transfer to any other app of information received from Google APIs will
        adhere to the{' '}
        <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-color)' }}>
          Google API Services User Data Policy
        </a>
        , including the Limited Use requirements. Specifically:
      </p>
      <ul style={{ paddingLeft: '24px', margin: '12px 0' }}>
        <li>We only use Google user data to provide and improve the workflow automation features you configure.</li>
        <li>We do not use Google user data for serving advertisements.</li>
        <li>We do not allow humans to read your Google user data unless required for security purposes, to comply with applicable law, or with your explicit consent.</li>
        <li>We do not transfer Google user data to third parties except as necessary to provide the Service, as required by law, or with your consent.</li>
      </ul>

      <SectionHeading>8. Children's Privacy</SectionHeading>
      <p>
        The Service is not intended for use by anyone under the age of 16. We do not knowingly
        collect personal information from children.
      </p>

      <SectionHeading>9. Changes to This Policy</SectionHeading>
      <p>
        We may update this Privacy Policy from time to time. We will notify you of material changes
        by posting the new policy on this page and updating the "Last updated" date. Your continued
        use of the Service after changes constitutes acceptance of the updated policy.
      </p>

      <SectionHeading>10. Contact Us</SectionHeading>
      <p>
        If you have questions about this Privacy Policy or your data, contact us at:{' '}
        <a href="mailto:privacy@xavvy.ai" style={{ color: 'var(--accent-color)' }}>privacy@xavvy.ai</a>
      </p>
    </LegalPage>
  );
};


// ── Terms of Service ───────────────────────────────────────────────────────
export const TermsOfServicePage = ({ branding }) => {
  const brandName = branding?.app_name || 'xavvy';
  return (
    <LegalPage title="Terms of Service" branding={branding}>
      <p>
        These Terms of Service ("Terms") govern your use of the {brandName} workflow automation
        platform and related services (the "Service") provided by {brandName} ("we", "us", or "our").
        By accessing or using the Service, you agree to be bound by these Terms.
      </p>

      <SectionHeading>1. Acceptance of Terms</SectionHeading>
      <p>
        By creating an account or using the Service, you confirm that you are at least 16 years old
        and agree to comply with these Terms. If you are using the Service on behalf of an organization,
        you represent that you have the authority to bind that organization.
      </p>

      <SectionHeading>2. Description of Service</SectionHeading>
      <p>
        {brandName} is a visual workflow automation platform that allows you to create, manage, and
        execute automated workflows by connecting nodes on a drag-and-drop canvas. The Service includes
        integration with third-party services via API connections and OAuth-based credential linking.
      </p>

      <SectionHeading>3. Your Account</SectionHeading>
      <ul style={{ paddingLeft: '24px', margin: '12px 0' }}>
        <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
        <li>You are responsible for all activities that occur under your account.</li>
        <li>You must notify us immediately of any unauthorized use of your account.</li>
        <li>We reserve the right to suspend or terminate accounts that violate these Terms.</li>
      </ul>

      <SectionHeading>4. Acceptable Use</SectionHeading>
      <p>You agree not to use the Service to:</p>
      <ul style={{ paddingLeft: '24px', margin: '12px 0' }}>
        <li>Violate any applicable law or regulation</li>
        <li>Infringe on the rights of others, including intellectual property rights</li>
        <li>Distribute malware, spam, or other harmful content</li>
        <li>Attempt to gain unauthorized access to other systems or data through the Service</li>
        <li>Interfere with or disrupt the Service or its infrastructure</li>
        <li>Use the Service to store or transmit content that is illegal, defamatory, or obscene</li>
        <li>Exceed rate limits or abuse API integrations in a way that impacts other users</li>
      </ul>

      <SectionHeading>5. Third-Party Integrations</SectionHeading>
      <p>
        The Service allows you to connect third-party accounts (Google, Microsoft, Slack, GitHub, and
        others) via OAuth or API credentials. When you connect a third-party service:
      </p>
      <ul style={{ paddingLeft: '24px', margin: '12px 0' }}>
        <li>You authorize {brandName} to access and interact with the third-party service on your behalf, limited to the scopes you approve.</li>
        <li>You are responsible for complying with the third-party service's terms of use.</li>
        <li>We are not responsible for the availability, accuracy, or conduct of third-party services.</li>
        <li>You can revoke third-party access at any time by deleting the credential in {brandName} or through the third-party provider's settings.</li>
      </ul>

      <SectionHeading>6. Data Ownership</SectionHeading>
      <ul style={{ paddingLeft: '24px', margin: '12px 0' }}>
        <li><strong>Your data</strong>: You retain all rights to your workflow definitions, run data, and any content processed by your workflows. We claim no ownership over your data.</li>
        <li><strong>Self-hosted</strong>: For self-hosted deployments, your data resides entirely on your infrastructure. We have no access to it.</li>
        <li><strong>Cloud-hosted</strong>: For our managed Cloud offering, we store your data securely and process it only to provide the Service.</li>
      </ul>

      <SectionHeading>7. Intellectual Property</SectionHeading>
      <p>
        The Service, including its source code (where not open-sourced), design, documentation, and
        branding, is the intellectual property of {brandName}. The open-source components are licensed
        under their respective open-source licenses. Your use of the Service does not grant you any
        ownership of our intellectual property.
      </p>

      <SectionHeading>8. Service Availability</SectionHeading>
      <p>
        We strive to maintain high availability but do not guarantee uninterrupted access. The Service
        may be temporarily unavailable due to maintenance, updates, or circumstances beyond our control.
        For self-hosted deployments, availability is your responsibility.
      </p>

      <SectionHeading>9. Limitation of Liability</SectionHeading>
      <p>
        To the maximum extent permitted by law, {brandName} shall not be liable for any indirect,
        incidental, special, consequential, or punitive damages, or any loss of profits or revenues,
        whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible
        losses, resulting from your use of the Service.
      </p>

      <SectionHeading>10. Disclaimer of Warranties</SectionHeading>
      <p>
        The Service is provided "as is" and "as available" without warranties of any kind, whether
        express or implied, including but not limited to implied warranties of merchantability, fitness
        for a particular purpose, and non-infringement.
      </p>

      <SectionHeading>11. Termination</SectionHeading>
      <p>
        You may stop using the Service at any time. We may suspend or terminate your access if you
        violate these Terms. Upon termination, your right to use the Service ceases immediately.
        Provisions that by their nature should survive termination (including limitations of liability
        and intellectual property) will survive.
      </p>

      <SectionHeading>12. Changes to These Terms</SectionHeading>
      <p>
        We may modify these Terms from time to time. We will notify you of material changes by updating
        the "Last updated" date. Your continued use of the Service after changes constitutes acceptance
        of the revised Terms.
      </p>

      <SectionHeading>13. Governing Law</SectionHeading>
      <p>
        These Terms are governed by and construed in accordance with the laws of the jurisdiction in
        which {brandName} operates, without regard to conflict of law principles.
      </p>

      <SectionHeading>14. Contact Us</SectionHeading>
      <p>
        If you have questions about these Terms, contact us at:{' '}
        <a href="mailto:legal@xavvy.ai" style={{ color: 'var(--accent-color)' }}>legal@xavvy.ai</a>
      </p>
    </LegalPage>
  );
};
